import { ShieldCheck, HeartPulse, CarFront, Building, FileWarning, Plane, UserCheck, CheckCircle2 } from "lucide-react";
import PageHero, { ScrollCta } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";

const COVERAGES = [
  { icon: ShieldCheck, title: "Term Life Insurance", desc: "Maximum cover at minimum cost — income protection for the people who depend on you." },
  { icon: HeartPulse, title: "Health & Mediclaim", desc: "Cashless hospitalisation cover for your family, with critical-illness riders where they matter." },
  { icon: CarFront, title: "Motor Insurance", desc: "Comprehensive own-damage and third-party cover for cars, two-wheelers and fleets." },
  { icon: Building, title: "Property Insurance", desc: "Fire, burglary and natural-calamity cover for homes, shops, offices and factories." },
  { icon: FileWarning, title: "Commercial Liability", desc: "Professional and product liability shields for businesses that face the public." },
  { icon: Plane, title: "Travel Insurance", desc: "Medical emergencies, delays and lost baggage — covered when you are far from home." },
  { icon: UserCheck, title: "Keyman Insurance", desc: "Protect the enterprise from the loss of the people it cannot afford to lose." },
];

const INSURANCE_SERVICES = [
  "Term Life Insurance", "Health & Mediclaim", "Motor Insurance", "Property Insurance",
  "Commercial Liability", "Travel Insurance", "Keyman Insurance",
];

export default function Insurance() {
  return (
    <div data-testid="insurance-page">
      <PageHero
        eyebrow="Protection Advisory"
        title={["Insurance", "Solutions."]}
        sub="Comprehensive protection for families and business assets — advised independently, bought at the right price, and settled without fights."
      >
        <ScrollCta href="#quote" label="Get a Quick Quote" testId="insurance-quote-btn" />
      </PageHero>

      {/* COVERAGE GRID */}
      <section className="py-16 sm:py-24 lg:py-28" data-testid="insurance-coverage-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">Coverage Suite</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Every risk that can hurt you, covered.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {COVERAGES.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.05}>
                <div className="card-lift h-full rounded-3xl border border-slate-200 bg-white p-7" data-testid={`insurance-card-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <c.icon className="h-8 w-8 text-amber-600" />
                  <h3 className="mt-6 font-display text-lg font-extrabold text-navy">{c.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{c.desc}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.35}>
              <div className="grain flex h-full flex-col justify-between rounded-3xl bg-navy p-7" data-testid="insurance-cta-tile">
                <ShieldCheck className="h-8 w-8 text-gold" />
                <div>
                  <h3 className="mt-8 font-display text-xl font-extrabold text-white">Not sure what you need?</h3>
                  <p className="mt-2 text-sm text-white/65">
                    A 15-minute protection review maps every gap in your family and business cover.
                  </p>
                  <a href="#quote" className="mt-5 inline-block rounded-full bg-gold px-6 py-3 text-sm font-bold text-navy transition hover:-translate-y-0.5" data-testid="insurance-review-cta">
                    Book a Review
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROTECTION BAND */}
      <section className="border-y border-slate-200 bg-[#F8FAFC] py-16 sm:py-20" data-testid="insurance-protection-band">
        <div className="container-x grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <Reveal>
            <p className="eyebrow">Why it matters</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              One policy bought right is worth ten bought in a hurry.
            </h2>
            <p className="mt-5 max-w-xl text-slate-600">
              Insurance fails at claim time, not buying time. We design your covers around what could actually go wrong,
              disclose everything up front, and stay on your side of the table when a claim needs to be pushed through.
            </p>
          </Reveal>
          <div className="space-y-4">
            {[
              "Independent advice across leading insurers — no single-company bias",
              "Claim settlement support for the life of the policy",
              "Annual cover reviews as your family and business grow",
            ].map((point, i) => (
              <Reveal key={point} delay={0.1 + i * 0.08}>
                <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
                  <p className="text-sm font-bold text-navy">{point}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section id="quote" className="py-16 sm:py-24" data-testid="insurance-quote-section">
        <div className="container-x grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow">Quick Quote</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Get a quick quote.
            </h2>
            <p className="mt-5 max-w-md text-slate-600">
              Pick the cover, leave your number — the insurance desk calls back with indicative premiums from multiple
              insurers on the same call.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl sm:p-8" data-testid="insurance-quote-form-card">
              <LeadForm
                source="insurance-page"
                fields={["name", "phone", "email", "service", "message"]}
                services={INSURANCE_SERVICES}
                cta="Get a Quick Quote"
                idPrefix="insurance-quote"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
